import PersistentLandingSlot from '../src/components/main/PersistentLandingSlot';
import Casting from '../src/../src/components/casting/casting';
import HdmiInput from '../src/components/hdmi/HdmiInput';
import Youtube from '../src/../src/components/youtube/youtube';
import Netflix from '../src/../src/components/netflix/netflix';
import Internet from '../src/../src/components/internet/internet';
import PortalRoute from '../src/components/portal/portalMenu';
import BaselineChannels from '../src/components/channels/channelsList';
import NewsRoute from '../src/components/news/newsList';
import WeatherRoute from '../src/components/weather/weather';
import TimeRoute from '../src/components/time/time';
import ServerDown from 'components/serverDown/serverDown';
import SettingsRoute from '../src/components/settings/settings';
import WarwickSettings from '../src/components/main/warwick/settings';
import ProxyLoadingRoute from '../src/components/registration/errorPages/loading';
import RegistrationErrorRoute from './components/registration/errorPages/deviceRegistrationError';
import PrimeVideo from '../src/../src/components/primevideo/primevideo';
import Storages from 'storages';
import StorageKeys from 'enum/storages.enum';
import { Landing as LandingEnum } from 'enum/design';
import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import Route from 'enum/route.enum';
import EventBus from 'utils/eventBus';
import { isRadioMenuAvailable } from 'utils/menuList';
import WarwickInfo from 'components/main/warwick/about';
import WarwickWeather from '../src/components/main/warwick/weather';
import WarwickNews from '../src/components/main/warwick/news';
import WarwickWelcome from '../src/components/main/warwick/welcome';
import WarwickNewDesign from 'components/main/warwick/warwick-redesign';
import WarwickV1Channels from 'components/main/warwick/tv';

const RouteResolver = ({ components }) => {
  const [Component, setComponent] = useState(null);

  useEffect(() => {
    const activeTemplate = Storages.MainStorage.getItem(StorageKeys.Keys.ACTIVETEMPLATE);

    if (activeTemplate === LandingEnum.Warwick) {
      setComponent(() => components[LandingEnum.Warwick]);
    } else if (activeTemplate === LandingEnum.WarwickV1) {
      setComponent(() => components[LandingEnum.WarwickV1] || components[LandingEnum.Warwick]);
    } else {
      setComponent(() => components[LandingEnum.BaseLine]);
    }
  }, [components]);

  if (!Component) {
    return <div></div>;
  }

  return <Component />;
};

const useRadioAvailability = () => {
  const [radioAvailable, setRadioAvailable] = useState(() => isRadioMenuAvailable());

  useEffect(() => {
    const refreshSubscription = EventBus.subscribe('refresh', () => {
      setRadioAvailable(isRadioMenuAvailable());
    });

    return () => refreshSubscription.unsubscribe();
  }, []);

  return radioAvailable;
};

const WarwickLiveTvRoute = () => <WarwickNewDesign isRadio={false} />;

const WarwickRadioRoute = () => {
  const radioAvailable = useRadioAvailability();

  if (!radioAvailable) {
    return <Navigate to={Route.route.LANDING} replace />;
  }

  return <WarwickNewDesign isRadio={true} />;
};

const BaselineTvRoute = () => <BaselineChannels isRadio={false} />;

const BaselineRadioRoute = () => {
  const radioAvailable = useRadioAvailability();

  if (!radioAvailable) {
    return <Navigate to={Route.route.LANDING} replace />;
  }

  return <BaselineChannels isRadio={true} />;
};

const WarwickV1TvRoute = () => <WarwickV1Channels isRadio={false} />;

const WarwickV1RadioRoute = () => {
  const radioAvailable = useRadioAvailability();

  if (!radioAvailable) {
    return <Navigate to={Route.route.LANDING} replace />;
  }

  return <WarwickV1Channels isRadio={true} />;
};

/** Paths where the persistent Landing shell should be visible */
export function isPersistentLandingPath(pathname) {
  if (pathname === Route.route.LANDING) {
    return true;
  }
  // BaseLine reuses Landing for /welcome; Warwick layouts have their own welcome screen
  if (pathname === Route.route.WELCOME) {
    const template = Storages.MainStorage.getItem(StorageKeys.Keys.ACTIVETEMPLATE);
    return template !== LandingEnum.Warwick && template !== LandingEnum.WarwickV1;
  }
  return false;
}

const routes = () => {
  return [
    {
      path: 'landing',
      // Mounted once in App (keep-alive); slot avoids remount on every visit
      element: <PersistentLandingSlot />,
    },
    {
      path: 'welcome',
      element: (
        <RouteResolver
          components={{
            [LandingEnum.Warwick]: WarwickWelcome,
            [LandingEnum.WarwickV1]: WarwickWelcome,
            [LandingEnum.BaseLine]: PersistentLandingSlot,
          }}
        />
      ),
    },
    {
      path: 'portal',
      element: (
        <RouteResolver
          components={{
            [LandingEnum.Warwick]: WarwickInfo,
            [LandingEnum.WarwickV1]: WarwickInfo,
            [LandingEnum.BaseLine]: PortalRoute,
          }}
        />
      ),
    },
    {
      path: 'channel',
      element: (
        <RouteResolver
          components={{
            [LandingEnum.Warwick]: WarwickLiveTvRoute,
            [LandingEnum.WarwickV1]: WarwickV1TvRoute,
            [LandingEnum.BaseLine]: BaselineTvRoute,
          }}
        />
      ),
    },
    {
      path: 'radio',
      element: (
        <RouteResolver
          components={{
            [LandingEnum.Warwick]: WarwickRadioRoute,
            [LandingEnum.WarwickV1]: WarwickV1RadioRoute,
            [LandingEnum.BaseLine]: BaselineRadioRoute,
          }}
        />
      ),
    },
    {
      path: 'news',
      element: (
        <RouteResolver
          components={{
            [LandingEnum.Warwick]: WarwickNews,
            [LandingEnum.WarwickV1]: WarwickNews,
            [LandingEnum.BaseLine]: NewsRoute,
          }}
        />
      ),
    },
    {
      path: 'weather',
      element: (
        <RouteResolver
          components={{
            [LandingEnum.Warwick]: WarwickWeather,
            [LandingEnum.WarwickV1]: WarwickWeather,
            [LandingEnum.BaseLine]: WeatherRoute,
          }}
        />
      ),
    },
    {
      path: 'time',
      element: <TimeRoute />,
    },
    {
      path: 'settings',
      element: (
        <RouteResolver
          components={{
            [LandingEnum.Warwick]: WarwickSettings,
            [LandingEnum.WarwickV1]: WarwickSettings,
            [LandingEnum.BaseLine]: SettingsRoute,
          }}
        />
      ),
    },
    {
      path: '404',
      element: <ServerDown />,
    },
    {
      path: 'device_ip/notfound',
      element: <RegistrationErrorRoute />,
    },
    {
      path: 'casting',
      element: <Casting />,
    },
    {
      path: 'hdmi/:port',
      element: <HdmiInput />,
    },
    {
      path: 'source/:type/:number',
      element: <HdmiInput />,
    },
    {
      path: 'youtube',
      element: <Youtube />,
    },
    {
      path: 'netflix',
      element: <Netflix />,
    },
    {
      path: 'internet',
      element: <Internet />,
    },
    {
      path: 'primevideo',
      element: <PrimeVideo />,
    },
    {
      path: '/loading',
      element: <ProxyLoadingRoute />,
    },
    {
      path: '/',
      element: <ProxyLoadingRoute />,
    },
  ];
};
export default routes;
